import { useState } from "react";

const verificationSteps = [
  {
    num: "01",
    icon: "ri-file-text-line",
    title: "营业执照 & 资质认证",
    desc: "上传有效营业执照、生产许可证、质检报告，平台人工核验企业真实性",
    required: ["营业执照（彩色扫描件）", "生产许可证 / 行业资质", "产品质量检测报告", "法人身份证正反面"],
    color: "bg-orange-500",
    tag: "必须",
  },
  {
    num: "02",
    icon: "ri-building-4-line",
    title: "工厂 & 仓库实地核验",
    desc: "提交工厂实景照片及视频，平台审核员远程或实地核验生产能力与仓储条件",
    required: ["工厂生产车间实景照片（≥5张）", "仓库实景照片（≥3张）", "生产设备照片", "工厂外观及门牌照片"],
    color: "bg-red-500",
    tag: "必须",
  },
  {
    num: "03",
    icon: "ri-trademark-line",
    title: "自有品牌认证",
    desc: "只允许销售自有品牌商品，必须提供商标注册证书，严禁仿冒任何第三方品牌",
    required: ["商标注册证书（R标或TM标）", "品牌授权书（如有代理）", "商品原创设计说明", "声明：不仿冒任何第三方品牌"],
    color: "bg-amber-500",
    tag: "核心",
  },
  {
    num: "04",
    icon: "ri-shield-check-line",
    title: "品质承诺 & 货款托管",
    desc: "买家付款后资金由平台托管，买家确认收到高品质商品后，货款才会释放给工厂",
    required: ["签署《品质承诺书》", "同意货款托管协议", "提供售后保障方案", "接受平台抽检机制"],
    color: "bg-green-500",
    tag: "保障",
  },
];

const policies = [
  {
    icon: "ri-bank-line",
    title: "货款全程托管",
    desc: "买家付款后，资金由皇冠鸿运商城平台托管。买家签收并确认商品质量合格后，货款才会自动释放给工厂。保护买家权益，杜绝收款不发货。",
    color: "text-green-500",
    bg: "bg-green-50",
  },
  {
    icon: "ri-trademark-line",
    title: "自有品牌专属",
    desc: "平台只允许工厂销售自己注册商标的品牌商品。严禁仿冒、山寨任何知名品牌。违规工厂将被永久封号并承担法律责任。",
    color: "text-orange-500",
    bg: "bg-orange-50",
  },
  {
    icon: "ri-microscope-line",
    title: "品质抽检机制",
    desc: "平台定期对工厂商品进行随机抽检，委托第三方质检机构出具报告。抽检不合格将暂停销售资格，连续不合格将永久取消工厂会员资格。",
    color: "text-red-500",
    bg: "bg-red-50",
  },
  {
    icon: "ri-camera-line",
    title: "实地核验工厂",
    desc: "申请时须提交工厂及仓库实景照片和视频，平台审核员进行远程核验。对于大额订单工厂，平台保留实地走访核验的权利。",
    color: "text-teal-500",
    bg: "bg-teal-50",
  },
];

const applyStepsConfig = [
  { label: "基本信息", icon: "ri-user-line" },
  { label: "资质上传", icon: "ri-file-upload-line" },
  { label: "工厂核验", icon: "ri-building-4-line" },
  { label: "品牌认证", icon: "ri-trademark-line" },
  { label: "协议确认", icon: "ri-shield-check-line" },
];

interface UploadBoxProps {
  label: string;
  hint?: string;
}

function UploadBox({ label, hint }: UploadBoxProps) {
  const [uploaded, setUploaded] = useState(false);
  return (
    <div>
      <label className="text-gray-500 text-xs font-medium block mb-1.5">{label} <span className="text-red-500">*</span></label>
      <div
        onClick={() => setUploaded(true)}
        className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
          uploaded ? "border-green-400 bg-green-50" : "border-gray-200 hover:border-orange-300"
        }`}
      >
        {uploaded ? (
          <div className="flex items-center justify-center gap-2 text-green-500">
            <i className="ri-check-fill text-xl"></i>
            <span className="text-sm font-medium">已上传</span>
          </div>
        ) : (
          <>
            <i className="ri-upload-cloud-line text-gray-300 text-2xl mb-1 block"></i>
            <p className="text-gray-400 text-xs">点击上传</p>
            {hint && <p className="text-gray-300 text-xs mt-0.5">{hint}</p>}
          </>
        )}
      </div>
    </div>
  );
}

export default function FactoryMemberSection() {
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyStep, setApplyStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreedBrand, setAgreedBrand] = useState(false);

  const handleClose = () => {
    setApplyOpen(false);
    setApplyStep(1);
    setSubmitted(false);
    setAgreed(false);
    setAgreedBrand(false);
  };

  const handleSubmit = () => {
    setSubmitted(true);
    setTimeout(() => handleClose(), 3000);
  };

  return (
    <>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-14">
            <span className="inline-block bg-orange-100 text-orange-600 text-xs px-3 py-1 rounded-full mb-4 font-medium">
              <i className="ri-building-4-line mr-1"></i> 工厂直营专区
            </span>
            <h2
              className="text-3xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              皇冠鸿运商城只接受经过严格资质核验的正规工厂入驻，所有工厂只能销售自有注册品牌商品，
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              皇冠鸿运商城只接受经过严格资质核验的正规工厂入驻，所有工厂只能销售自有注册品牌商品，
              货款全程托管，买家确认收到高品质商品后才会放款，保障买卖双方权益。
            </p>
          </div>

          {/* Platform guarantee banner */}
          <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-2xl p-6 mb-12 text-white">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl flex-shrink-0">
                <i className="ri-shield-star-line text-white text-3xl"></i>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="font-bold text-xl mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                  平台核心承诺
                </h3>
                <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center sm:justify-start">
                  {[
                    "工厂资质人工核验",
                    "仓库实景照片审核",
                    "自有品牌强制认证",
                    "货款全程平台托管",
                    "买家确认后才放款",
                    "严禁仿冒任何品牌",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-1.5 text-white/90 text-sm">
                      <i className="ri-check-line text-yellow-300"></i>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setApplyOpen(true)}
                className="bg-white text-red-500 hover:bg-red-50 px-7 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-building-line mr-1"></i>申请入驻
              </button>
            </div>
          </div>

          {/* Verification steps */}
          <div className="mb-14">
            <h3
              className="text-xl font-bold text-gray-800 text-center mb-8"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              四步严格认证流程
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {verificationSteps.map((step) => (
                <div key={step.num} className="bg-gray-50 rounded-2xl p-5 relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-11 h-11 flex items-center justify-center ${step.color} rounded-xl flex-shrink-0`}>
                      <i className={`${step.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <span className="text-gray-300 text-xs font-bold">STEP {step.num}</span>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                          step.tag === "必须" ? "bg-red-100 text-red-500" :
                          step.tag === "核心" ? "bg-orange-100 text-orange-500" :
                          "bg-green-100 text-green-500"
                        }`}>{step.tag}</span>
                      </div>
                    </div>
                  </div>
                  <h4 className="text-gray-800 font-bold text-sm mb-2">{step.title}</h4>
                  <p className="text-gray-400 text-xs leading-relaxed mb-4">{step.desc}</p>
                  <div className="space-y-1.5">
                    {step.required.map((req) => (
                      <div key={req} className="flex items-start gap-2">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-orange-400 text-sm"></i>
                        </div>
                        <span className="text-gray-500 text-xs leading-snug">{req}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Platform policies */}
          <div className="mb-12">
            <h3
              className="text-xl font-bold text-gray-800 text-center mb-8"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              平台保障机制
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {policies.map((p) => (
                <div key={p.title} className={`${p.bg} rounded-2xl p-5`}>
                  <div className={`w-12 h-12 flex items-center justify-center bg-white rounded-xl mb-4`}>
                    <i className={`${p.icon} ${p.color} text-2xl`}></i>
                  </div>
                  <h4 className="text-gray-800 font-bold text-sm mb-2">{p.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Escrow flow diagram */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-12">
            <h3
              className="text-lg font-bold text-gray-800 text-center mb-8"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              货款托管流程 — 买卖双方全程受保护
            </h3>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {[
                { icon: "ri-user-line", label: "买家下单付款", sub: "资金进入平台托管账户", color: "bg-red-500" },
                { icon: "ri-arrow-right-line", label: "", sub: "", color: "", isArrow: true },
                { icon: "ri-bank-line", label: "平台托管资金", sub: "工厂无法直接提取货款", color: "bg-orange-500" },
                { icon: "ri-arrow-right-line", label: "", sub: "", color: "", isArrow: true },
                { icon: "ri-truck-line", label: "工厂发货", sub: "高品质商品按时发出", color: "bg-amber-500" },
                { icon: "ri-arrow-right-line", label: "", sub: "", color: "", isArrow: true },
                { icon: "ri-checkbox-circle-line", label: "买家确认收货", sub: "验收商品质量合格", color: "bg-teal-500" },
                { icon: "ri-arrow-right-line", label: "", sub: "", color: "", isArrow: true },
                { icon: "ri-money-cny-circle-line", label: "货款自动释放", sub: "资金打入工厂账户", color: "bg-green-500" },
              ].map((item, idx) => (
                item.isArrow ? (
                  <div key={idx} className="text-gray-300 text-2xl flex-shrink-0 hidden sm:block">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                ) : (
                  <div key={idx} className="flex flex-col items-center text-center flex-1 min-w-0">
                    <div className={`w-12 h-12 flex items-center justify-center ${item.color} rounded-full mb-2`}>
                      <i className={`${item.icon} text-white text-xl`}></i>
                    </div>
                    <p className="text-gray-700 font-semibold text-xs">{item.label}</p>
                    <p className="text-gray-400 text-xs mt-0.5 leading-tight">{item.sub}</p>
                  </div>
                )
              ))}
            </div>
            <div className="mt-6 bg-orange-50 border border-orange-100 rounded-xl p-4 text-center">
              <p className="text-orange-600 text-sm font-medium">
                <i className="ri-information-line mr-1"></i>
                如买家对商品质量有异议，可在收货后 <strong>7天内</strong> 发起纠纷，平台介入调查后决定货款归属
              </p>
            </div>
          </div>

          {/* Brand protection warning */}
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-2xl p-6 mb-10">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-xl flex-shrink-0">
                <i className="ri-error-warning-line text-white text-xl"></i>
              </div>
              <div>
                <h4 className="text-red-600 font-bold text-base mb-2">品牌保护红线 — 零容忍政策</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    "严禁销售仿冒、山寨任何知名品牌商品",
                    "严禁使用与知名品牌相似的商标或包装",
                    "严禁虚假宣传商品品质或产地",
                    "严禁以次充好、以假乱真",
                    "违规工厂将被永久封号并公示",
                    "情节严重者将被移交司法机关处理",
                  ].map((rule) => (
                    <div key={rule} className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-400 text-sm mt-0.5 flex-shrink-0"></i>
                      <span className="text-red-700 text-xs leading-snug">{rule}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Commission notice */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 flex items-center justify-center bg-amber-500 rounded-xl flex-shrink-0">
                <i className="ri-percent-line text-white text-xl"></i>
              </div>
              <div className="flex-1">
                <h4 className="text-amber-800 font-bold text-base mb-1">平台交易佣金说明</h4>
                <p className="text-amber-700 text-sm leading-relaxed mb-3">
                  平台对每笔成交订单收取 <strong className="text-amber-900 text-lg">1.6%</strong> 交易佣金（与阿里巴巴同类平台标准一致），佣金在货款放款时自动扣除，无需额外操作。
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { label: "佣金比例", value: "1.6%", sub: "每笔成交订单", icon: "ri-percent-line" },
                    { label: "扣除时机", value: "放款时自动扣除", sub: "无需手动操作", icon: "ri-time-line" },
                    { label: "到账金额", value: "订单金额 × 98.4%", sub: "实际到账工厂", icon: "ri-money-cny-circle-line" },
                  ].map(item => (
                    <div key={item.label} className="bg-white rounded-xl p-3 flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-amber-600 text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">{item.label}</p>
                        <p className="text-gray-800 font-bold text-sm">{item.value}</p>
                        <p className="text-gray-400 text-xs">{item.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-amber-600 text-xs mt-3">
                  <i className="ri-information-line mr-1"></i>
                  示例：订单金额 ¥1,000，平台佣金 ¥16（1.6%），工厂实际到账 ¥984。月费 ¥5 与佣金分开计算。
                </p>
              </div>
            </div>
          </div>

          {/* Pricing + CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-100 rounded-2xl p-6">
            <div className="flex items-center gap-6 flex-wrap justify-center sm:justify-start">
              <div className="text-center">
                <p className="text-orange-500 font-bold text-3xl">免费</p>
                <p className="text-gray-500 text-sm mt-0.5">前3个月</p>
              </div>
              <div className="text-gray-300 text-xl">→</div>
              <div className="text-center">
                <p className="text-gray-800 font-bold text-3xl">¥5<span className="text-base font-normal text-gray-400">/月</span></p>
                <p className="text-gray-500 text-sm mt-0.5">3个月后</p>
              </div>
              <div className="flex flex-col gap-1">
                {["无需绑卡，随时取消", "审核通过即刻开通", "成交佣金仅 1.6%"].map((t) => (
                  <div key={t} className="flex items-center gap-1.5 text-gray-500 text-xs">
                    <i className="ri-check-line text-green-500"></i>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => setApplyOpen(true)}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-10 py-4 rounded-2xl font-bold text-base transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-building-line mr-2"></i>免费申请工厂入驻
            </button>
          </div>
        </div>
      </section>

      {/* Apply Modal — 5 steps */}
      {applyOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={handleClose}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-gray-800 font-bold text-lg">申请工厂入驻</h3>
                <p className="text-gray-400 text-xs mt-0.5">
                  步骤 {applyStep} / {applyStepsConfig.length} — {applyStepsConfig[applyStep - 1]?.label}
                </p>
              </div>
              <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Step progress */}
            <div className="px-6 pt-5 pb-2">
              <div className="flex items-center gap-1">
                {applyStepsConfig.map((s, idx) => (
                  <div key={s.label} className="flex items-center gap-1 flex-1">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 transition-all ${
                      applyStep > idx + 1 ? "bg-green-500 text-white" :
                      applyStep === idx + 1 ? "bg-orange-500 text-white" :
                      "bg-gray-100 text-gray-400"
                    }`}>
                      {applyStep > idx + 1 ? <i className="ri-check-line"></i> : idx + 1}
                    </div>
                    {idx < applyStepsConfig.length - 1 && (
                      <div className={`flex-1 h-0.5 transition-all ${applyStep > idx + 1 ? "bg-green-400" : "bg-gray-100"}`}></div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-1">
                {applyStepsConfig.map((s) => (
                  <span key={s.label} className="text-gray-400 text-xs flex-1 text-center first:text-left last:text-right">{s.label}</span>
                ))}
              </div>
            </div>

            <div className="px-6 pb-6 pt-4">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                    <i className="ri-check-line text-green-500 text-4xl"></i>
                  </div>
                  <p className="text-gray-800 font-bold text-xl mb-2">申请已提交！</p>
                  <p className="text-gray-500 text-sm mb-4">我们将在 <strong>24小时内</strong> 完成资质审核</p>
                  <div className="bg-orange-50 rounded-xl p-4 text-left space-y-2">
                    {[
                      "审核结果将通过短信和邮件通知",
                      "审核通过后即可开始上架自有品牌商品",
                      "前3个月完全免费，无需任何费用",
                    ].map((t) => (
                      <div key={t} className="flex items-center gap-2 text-orange-600 text-sm">
                        <i className="ri-check-line flex-shrink-0"></i>
                        <span>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {/* Step 1: Basic info */}
                  {applyStep === 1 && (
                    <div className="space-y-4">
                      <div className="bg-orange-50 rounded-xl p-3 mb-2">
                        <p className="text-orange-600 text-xs font-medium">
                          <i className="ri-information-line mr-1"></i>
                          请填写真实信息，平台将进行人工核验，虚假信息将导致申请被拒
                        </p>
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">工厂/企业全称 <span className="text-red-500">*</span></label>
                        <input type="text" placeholder="请输入营业执照上的企业全称" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">法人代表姓名 <span className="text-red-500">*</span></label>
                        <input type="text" placeholder="请输入法人代表真实姓名" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">联系手机号 <span className="text-red-500">*</span></label>
                        <input type="tel" placeholder="请输入法人或负责人手机号" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">工厂所在省市 <span className="text-red-500">*</span></label>
                        <input type="text" placeholder="例：广东省深圳市龙华区" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">主营品类 <span className="text-red-500">*</span></label>
                        <select className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 outline-none cursor-pointer focus:border-orange-400">
                          <option value="">请选择主营品类</option>
                          <option>手机数码配件</option>
                          <option>家用电器</option>
                          <option>美妆护肤</option>
                          <option>服装鞋包</option>
                          <option>运动户外</option>
                          <option>食品生鲜</option>
                          <option>母婴玩具</option>
                          <option>家居家具</option>
                          <option>其他</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Certificates */}
                  {applyStep === 2 && (
                    <div className="space-y-4">
                      <div className="bg-red-50 rounded-xl p-3 mb-2">
                        <p className="text-red-600 text-xs font-medium">
                          <i className="ri-error-warning-line mr-1"></i>
                          以下证件为必须上传项，缺少任何一项将导致审核不通过
                        </p>
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">统一社会信用代码 <span className="text-red-500">*</span></label>
                        <input type="text" placeholder="18位统一社会信用代码" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 font-mono" />
                      </div>
                      <UploadBox label="营业执照（彩色扫描件）" hint="JPG/PNG/PDF，最大10MB" />
                      <UploadBox label="生产许可证 / 行业资质证书" hint="JPG/PNG/PDF，最大10MB" />
                      <UploadBox label="产品质量检测报告" hint="第三方机构出具，JPG/PNG/PDF" />
                      <UploadBox label="法人身份证正面" hint="JPG/PNG，最大5MB" />
                      <UploadBox label="法人身份证反面" hint="JPG/PNG，最大5MB" />
                    </div>
                  )}

                  {/* Step 3: Factory verification */}
                  {applyStep === 3 && (
                    <div className="space-y-4">
                      <div className="bg-orange-50 rounded-xl p-3 mb-2">
                        <p className="text-orange-600 text-xs font-medium">
                          <i className="ri-camera-line mr-1"></i>
                          请上传真实工厂及仓库照片，平台审核员将核验生产能力与仓储条件
                        </p>
                      </div>
                      <UploadBox label="工厂生产车间照片（至少5张）" hint="清晰展示生产设备和工人，JPG/PNG" />
                      <UploadBox label="仓库实景照片（至少3张）" hint="展示库存商品和仓储环境" />
                      <UploadBox label="工厂外观及门牌照片" hint="含门牌号，与营业执照地址一致" />
                      <UploadBox label="生产设备照片" hint="展示主要生产设备" />
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">工厂视频介绍（可选）</label>
                        <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-orange-300 transition-colors">
                          <i className="ri-video-upload-line text-gray-300 text-2xl mb-1 block"></i>
                          <p className="text-gray-400 text-xs">上传工厂介绍视频（MP4，最大100MB）</p>
                          <p className="text-gray-300 text-xs mt-0.5">视频可提高审核通过率</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Brand certification */}
                  {applyStep === 4 && (
                    <div className="space-y-4">
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2">
                        <p className="text-amber-700 text-xs font-medium">
                          <i className="ri-trademark-line mr-1"></i>
                          平台只允许销售自有注册商标品牌商品。严禁仿冒任何第三方品牌，违规将被永久封号。
                        </p>
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">自有品牌名称 <span className="text-red-500">*</span></label>
                        <input type="text" placeholder="请输入您的注册商标品牌名称" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">商标注册号 <span className="text-red-500">*</span></label>
                        <input type="text" placeholder="请输入商标注册证书上的注册号" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 font-mono" />
                      </div>
                      <UploadBox label="商标注册证书" hint="JPG/PNG/PDF，最大10MB" />
                      <UploadBox label="商品实物照片（至少3款）" hint="展示自有品牌商品，清晰显示品牌标识" />
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="brandPledge"
                            checked={agreedBrand}
                            onChange={(e) => setAgreedBrand(e.target.checked)}
                            className="mt-0.5 cursor-pointer flex-shrink-0"
                          />
                          <label htmlFor="brandPledge" className="text-red-700 text-xs leading-relaxed cursor-pointer">
                            <strong>品牌承诺声明：</strong>本人/本企业郑重承诺，所有在皇冠鸿运商城销售的商品均为自有注册商标品牌商品，
                            不仿冒、不山寨任何第三方品牌，不以次充好，不虚假宣传。如有违反，愿接受平台永久封号处理，
                            并承担相应法律责任。
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 5: Agreement */}
                  {applyStep === 5 && (
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                        <h4 className="text-gray-700 font-bold text-sm">确认申请信息</h4>
                        {[
                          { label: "会员类型", value: "工厂直营会员" },
                          { label: "免费期", value: "前3个月完全免费" },
                          { label: "续费价格", value: "¥5/月（可随时取消）" },
                          { label: "交易佣金", value: "1.6%（每笔成交订单）" },
                          { label: "货款机制", value: "平台托管，买家确认后放款" },
                          { label: "品牌要求", value: "仅限自有注册商标品牌" },
                          { label: "审核时效", value: "24小时内完成" },
                        ].map((item) => (
                          <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                            <span className="text-gray-400 text-xs">{item.label}</span>
                            <span className="text-gray-700 font-semibold text-xs">{item.value}</span>
                          </div>
                        ))}
                      </div>

                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="finalAgree"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            className="mt-0.5 cursor-pointer flex-shrink-0"
                          />
                          <label htmlFor="finalAgree" className="text-gray-500 text-xs leading-relaxed cursor-pointer">
                            我已阅读并同意《工厂会员服务协议》《平台品牌保护规则》《货款托管协议》，
                            了解前3个月免费期结束后将按 ¥5/月收费，并承诺遵守平台所有规则。
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  <div className="flex gap-3 mt-6">
                    {applyStep > 1 && (
                      <button
                        onClick={() => setApplyStep((s) => s - 1)}
                        className="flex-1 py-3 rounded-xl font-medium text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-arrow-left-line mr-1"></i>上一步
                      </button>
                    )}
                    <button
                      onClick={() => applyStep < 5 ? setApplyStep((s) => s + 1) : handleSubmit()}
                      disabled={applyStep === 4 && !agreedBrand || applyStep === 5 && !agreed}
                      className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                        (applyStep === 4 && !agreedBrand) || (applyStep === 5 && !agreed)
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                      }`}
                    >
                      {applyStep < 5 ? <>下一步 <i className="ri-arrow-right-line ml-1"></i></> : <>提交申请</>}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
